/**
 * Tiny REST client for the Shakthi Power Node/MySQL backend.
 *
 * Base URL comes from VITE_API_URL (set in a .env file), defaulting to the
 * local dev server. Every call has a short timeout and throws on failure, so
 * callers can `catch` and fall back to local data when the backend isn't
 * reachable (e.g. the static deployment with no server running).
 */
import { authStore, type SoftwareCode } from '@/context/auth'

const BASE: string =
  (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:4000/api'

// Railway backends cold-start (sleep when idle), so the first request after a
// while can take several seconds. A short timeout caused real logins to abort
// and look like "invalid credentials", so we allow more headroom.
const TIMEOUT_MS = 15000

export class ApiError extends Error {
  status?: number
  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

// When an authenticated request comes back 401 (expired/invalid token), the
// session is dead: clear it and send the user to the login screen. We skip the
// login call itself (its 401 just means wrong credentials, handled in the page)
// and avoid a redirect loop when already on /login.
function handleUnauthorized(path: string): void {
  if (path === '/auth/login') return
  authStore.clear()
  if (window.location.pathname !== '/login') window.location.assign('/login')
}

async function request<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)
  const token = authStore.getToken()
  try {
    const res = await fetch(BASE + path, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    })
    const text = await res.text()
    const data = text ? JSON.parse(text) : null
    if (!res.ok) {
      const errorMsg = (data && data.error) || res.statusText
      console.error(`[API Error] ${options.method || 'GET'} ${path} - ${res.status}: ${errorMsg}`)
      // A stale token (issued before software access existed, or before an
      // admin just changed this user's access) reads as "your login isn't
      // valid for this" rather than an ordinary permission error — same
      // recovery as a 401: clear the session and send them back to log in,
      // which issues a fresh token with the current access baked in.
      if (res.status === 401 || (res.status === 403 && data?.code === 'SOFTWARE_ACCESS_DENIED')) {
        handleUnauthorized(path)
      }
      throw new ApiError(errorMsg, res.status)
    }
    return data as T
  } finally {
    clearTimeout(timer)
  }
}

export interface ApiUser {
  id: string
  code?: string
  name: string
  email: string
  role: 'admin' | 'employee'
  phone?: string
  region?: string
  status?: string
  softwareAccess?: SoftwareCode[]
}

export type UploadFolder = 'leads' | 'users' | 'logos' | 'payments' | 'agreements'

export interface UploadResult {
  key: string
  url: string
  size: number
}

// Upload a single image to object storage (R2) and get back its public URL.
// Uses a longer timeout than JSON calls since files take longer than 4s.
async function uploadImage(folder: UploadFolder, file: File): Promise<UploadResult> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 30000)
  const token = authStore.getToken()
  const form = new FormData()
  form.append('file', file)
  try {
    const res = await fetch(`${BASE}/uploads/${folder}`, {
      method: 'POST',
      signal: controller.signal,
      // NOTE: do NOT set Content-Type; the browser sets the multipart boundary.
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    })
    const text = await res.text()
    const data = text ? JSON.parse(text) : null
    if (!res.ok) {
      if (res.status === 401) handleUnauthorized(`/uploads/${folder}`)
      throw new ApiError((data && data.error) || res.statusText, res.status)
    }
    return data as UploadResult
  } finally {
    clearTimeout(timer)
  }
}

// Downloads the full-database SQL backup and triggers a browser save-as.
// A plain <a href> download can't carry the Authorization header the admin-
// only endpoint requires, so this fetches it as a blob and saves that
// instead — same reasoning as uploadImage's custom fetch below. Generating
// the backup means 34 tables' worth of sequential queries against the live
// DB, so this gets a much longer timeout than a normal JSON call.
async function downloadBackupSql(): Promise<void> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 180000)
  const token = authStore.getToken()
  try {
    const res = await fetch(`${BASE}/admin/backup.sql`, {
      signal: controller.signal,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!res.ok) {
      if (res.status === 401) handleUnauthorized('/admin/backup.sql')
      let msg = res.statusText
      try { msg = (await res.json()).error || msg } catch { /* body wasn't JSON */ }
      throw new ApiError(msg, res.status)
    }
    const disposition = res.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename="([^"]+)"/)
    const filename = match ? match[1] : `shakthi_power_backup_${new Date().toISOString().slice(0, 10)}.sql`
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } finally {
    clearTimeout(timer)
  }
}

// Renders HTML to PDF server-side (real headless Chromium — see
// pdfService.js) and triggers a direct browser download of it. Bypasses the
// generic JSON request() helper since the response body is the PDF file
// itself, not JSON — same reasoning as downloadBackupSql above. PDF
// rendering (a real browser launch) is slower than a normal API call, so
// this gets a longer timeout. Shared by both Proposals and Agreements,
// whose "Download PDF" buttons both used to render blank via html2pdf.js/
// html2canvas (unreliable at capturing background images / consistently
// across complex layouts).
async function downloadServerRenderedPdf(path: string, htmlContent: string, filename: string): Promise<void> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 60000)
  const token = authStore.getToken()
  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ htmlContent }),
    })
    if (!res.ok) {
      if (res.status === 401) handleUnauthorized(path)
      let msg = res.statusText
      try { msg = (await res.json()).error || msg } catch { /* body wasn't JSON */ }
      throw new ApiError(msg, res.status)
    }
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } finally {
    clearTimeout(timer)
  }
}

const downloadProposalPdf = (proposalId: string, htmlContent: string, filename: string) =>
  downloadServerRenderedPdf(`/proposals/${proposalId}/pdf`, htmlContent, filename)

const downloadAgreementPdf = (agreementId: string, htmlContent: string, filename: string) =>
  downloadServerRenderedPdf(`/agreements/${agreementId}/pdf`, htmlContent, filename)

const downloadPaymentPdf = (paymentId: string, htmlContent: string, filename: string) =>
  downloadServerRenderedPdf(`/payments/${paymentId}/pdf`, htmlContent, filename)

export const api = {
  baseUrl: BASE,
  get: <T = any>(path: string) => request<T>(path),
  post: <T = any>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T = any>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T = any>(path: string) => request<T>(path, { method: 'DELETE' }),
  uploadImage,
  downloadProposalPdf,
  downloadAgreementPdf,
  downloadPaymentPdf,
  downloadBackupSql,
  deleteImage: (key: string) =>
    request<{ ok: true }>('/uploads', { method: 'DELETE', body: JSON.stringify({ key }) }),
  login: (email: string, password: string) =>
    request<{ token: string; user: ApiUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  forgotPassword: (email: string) =>
    request<{ ok: true; message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<{ ok: true }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),
  changePassword: (userId: string, currentPassword: string, newPassword: string) =>
    request<{ ok: true }>(`/users/${userId}/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  // Grand Shakthi Power's own Role Master, read cross-database — populates
  // the "Grand Shakthi Power Role" dropdown in Employee Master. Empty array
  // if GSP_DATABASE_URL isn't configured server-side, never an error.
  gspRoles: () => request<{ id: string; name: string; description?: string }[]>('/gsp-roles'),
  emailInvoice: (invoiceId: string, data: unknown) =>
    request<{ ok: true; sentTo: string }>(`/invoices/${invoiceId}/email`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  emailLead: (leadId: string, data: { subject: string; body: string; to?: string }) =>
    request<{ ok: true; sentTo: string }>(`/leads/${leadId}/email`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  emailProposal: (
    proposalId: string,
    data: { subject: string; body: string; to: string; htmlContent?: string }
  ) =>
    request<{ ok: true; sentTo: string }>(`/proposals/${proposalId}/email`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  emailPayment: (
    paymentId: string,
    data: { subject: string; body: string; to?: string; htmlContent?: string }
  ) =>
    request<{ ok: true; sentTo: string }>(`/payments/${paymentId}/email`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}
